import { logError } from "@jetblue/dotcom-fe-logger";

const REQUIRED_CONSENT_CATEGORY = 1 as const;
const FUNCTIONAL_CONSENT_CATEGORY = 2 as const;
const ADVERTISING_CONSENT_CATEGORY = 3 as const;

export type RawDecision = number[];
export type ConsentDecisionEmission = "changed" | "initial" | "unchanged";

type Decision = {
  advertising: undefined | boolean;
  functional: undefined | boolean;
  raw: RawDecision;
  required: undefined | boolean;
};

let currentDecision: Decision = {
  advertising: undefined,
  functional: undefined,
  raw: [],
  required: undefined,
};

const subscribers: {
  callback: (decision: typeof currentDecision) => void;
  label: string;
}[] = [];

const decisionKnown: PromiseWithResolvers<void> = Promise.withResolvers();

const notifySubscriber = (subscriber: (typeof subscribers)[number]) => {
  try {
    subscriber.callback(currentDecision);
  } catch (error) {
    void logError({
      error,
      message: `The ${subscriber.label} consent subscriber threw while reacting to a decision`,
    });
  }
};

export function subscribeToUserConsentDecision(
  callback: (typeof subscribers)[number]["callback"],
  label: string,
) {
  const subscriber = { callback, label };
  notifySubscriber(subscriber);
  subscribers.push(subscriber);

  return () => {
    const index = subscribers.indexOf(subscriber);
    if (index !== -1) {
      subscribers.splice(index, 1);
    }
  };
}
export function getUserConsentDecision() {
  return currentDecision;
}
export function whenUserConsentDecisionKnown() {
  return decisionKnown.promise;
}
export function emit(newRawDecision: RawDecision): ConsentDecisionEmission {
  decisionKnown.resolve();

  const previousDecisionWasKnown = currentDecision.required !== undefined;

  const newDecision: Decision = {
    advertising: newRawDecision.includes(ADVERTISING_CONSENT_CATEGORY),
    functional: newRawDecision.includes(FUNCTIONAL_CONSENT_CATEGORY),
    raw: [...newRawDecision],
    required: newRawDecision.includes(REQUIRED_CONSENT_CATEGORY),
  };

  if (
    newDecision.advertising === currentDecision.advertising &&
    newDecision.functional === currentDecision.functional &&
    newDecision.required === currentDecision.required
  ) {
    currentDecision = { ...currentDecision, raw: newDecision.raw };
    return "unchanged";
  }

  currentDecision = newDecision;

  // eslint-disable-next-line unicorn/no-useless-spread -- the copy is load-bearing, not cosmetic
  for (const subscriber of [...subscribers]) {
    if (subscribers.includes(subscriber)) {
      notifySubscriber(subscriber);
    }
  }

  return previousDecisionWasKnown ? "changed" : "initial";
}
